import React, { useState, useEffect } from 'react';
import { useFamilyTree } from '../contexts/FamilyTreeContext';
import FamilyTreeCanvas, { formatDate } from '../components/FamilyTreeCanvas';
import { DataService } from '../services/DataService';
import { useAuth } from '../contexts/AuthContext';
import Modal from '../components/Modal';
import IndividualForm from '../components/IndividualForm';
import RelationshipForm from '../components/RelationshipForm';
import { Individual, FamilyUnit } from '../models/types';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';

type ViewType = 'list' | 'tree';

const FamilyTreePage: React.FC = () => {
  const { familyTree, loading, refreshData } = useFamilyTree();
  const { user, logout } = useAuth();
  const [canvasSize, setCanvasSize] = useState({ width: 1000, height: 600 });
  const [activeView, setActiveView] = useState<ViewType>('tree');
  
  // Modals state for individuals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedIndividual, setSelectedIndividual] = useState<Individual | null>(null);
  const [individuals, setIndividuals] = useState<Individual[]>([]);
  
  // Modals state for relationships
  const [isCreateFamilyUnitModalOpen, setIsCreateFamilyUnitModalOpen] = useState(false);
  const [isAddChildModalOpen, setIsAddChildModalOpen] = useState(false);
  const [selectedFamilyUnit, setSelectedFamilyUnit] = useState<FamilyUnit | null>(null);
  const [familyUnits, setFamilyUnits] = useState<FamilyUnit[]>([]);
  
  // Load data when family tree changes
  useEffect(() => {
    if (familyTree) {
      if (familyTree.individuals) {
        setIndividuals(familyTree.individuals);
      }
      if (familyTree.familyUnits) {
        setFamilyUnits(familyTree.familyUnits);
      }
    }
  }, [familyTree]);
  
  useEffect(() => {
    const handleResize = () => {
      const containerWidth = Math.min(window.innerWidth - 40, 1200);
      setCanvasSize({
        width: containerWidth,
        height: window.innerHeight - 250 // Adjusted for header and controls
      });
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const handleGenerateSampleData = async () => {
    if (window.confirm('This will clear any existing data. Are you sure you want to continue?')) {
      await DataService.generateSampleData();
      await refreshData();
    }
  };
  
  const handleClearData = async () => {
    if (window.confirm('This will clear all family tree data. Are you sure?')) {
      // Implementation will be different for MySQL
      // Here we'll simply keep the users table and clear all data tables
      // TODO: Implement clear data operation for MySQL
      await refreshData();
    }
  };
  
  const handleExportData = () => {
    const dataStr = JSON.stringify(familyTree, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `family_tree_${new Date().toISOString().slice(0, 10)}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const handleExportPDF = async () => {
    const canvasContainer = document.querySelector('.canvas-container');
    if (!canvasContainer) return;
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: 'a4' });
    const canvas = await html2canvas(canvasContainer as HTMLElement, { backgroundColor: '#fff' });
    const imgData = canvas.toDataURL('image/png');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, pageHeight);
    pdf.save(`family_tree_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const handleExportImage = async () => {
    const stageEl = document.querySelector('.canvas-container canvas');
    if (!stageEl) return;
    const canvas = stageEl as HTMLCanvasElement;
    const imgURL = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = imgURL;
    link.download = `family_tree_${new Date().toISOString().slice(0, 10)}.png`;
    link.click();
  };

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();
    const individualsSheet = XLSX.utils.json_to_sheet(familyTree.individuals.map(ind => ({
      ID: ind.id,
      FirstName: ind.firstName,
      MiddleName: ind.middleName,
      LastName: ind.lastName,
      Gender: ind.gender,
      BirthDate: ind.birthDate,
      DeathDate: ind.deathDate,
      BirthPlace: ind.birthPlace,
      Occupation: ind.occupation,
      Education: ind.education,
      Notes: ind.notes,
      IsDeceased: ind.isDeceased,
      Tags: Array.isArray(ind.tags) ? ind.tags.join(', ') : '',
      Photos: Array.isArray(ind.photos) ? ind.photos.join(', ') : ''
    })));
    XLSX.utils.book_append_sheet(wb, individualsSheet, 'Individuals');
    const familyUnitsSheet = XLSX.utils.json_to_sheet(familyTree.familyUnits.map(fu => ({
      ID: fu.id,
      Partner1: fu.partners[0],
      Partner2: fu.partners[1],
      RelationshipType: fu.relationshipType,
      MarriageDate: fu.marriageDate,
      DivorceDate: fu.divorceDate,
      Notes: fu.notes
    })));
    XLSX.utils.book_append_sheet(wb, familyUnitsSheet, 'FamilyUnits');
    const childSheet = XLSX.utils.json_to_sheet(familyTree.childRelationships.map(cr => ({
      ID: cr.id,
      ChildID: cr.childId,
      FamilyUnitID: cr.familyUnitId,
      ParentalType: cr.parentalType,
      Notes: cr.notes
    })));
    XLSX.utils.book_append_sheet(wb, childSheet, 'ChildRelationships');
    const emotionalSheet = XLSX.utils.json_to_sheet(familyTree.emotionalRelationships.map(er => ({
      ID: er.id,
      From: er.fromId,
      To: er.toId,
      Tag: er.tag,
      Notes: er.notes
    })));
    XLSX.utils.book_append_sheet(wb, emotionalSheet, 'EmotionalRelationships');
    XLSX.writeFile(wb, `family_tree_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // Open edit modal for an individual
  const handleEditIndividual = (individual: Individual) => {
    setSelectedIndividual(individual);
    setIsEditModalOpen(true);
  };

  // Open delete confirmation for an individual
  const handleDeleteIndividual = (individual: Individual) => {
    setSelectedIndividual(individual);
    setIsDeleteModalOpen(true);
  };

  // Confirm and execute delete
  const confirmDelete = async () => {
    if (selectedIndividual) {
      await DataService.deleteIndividual(selectedIndividual.id);
      await refreshData();
      setIsDeleteModalOpen(false);
      setSelectedIndividual(null);
    }
  };

  // Open add child modal for a specific family unit
  const handleAddChildToFamily = (familyUnit: FamilyUnit) => {
    setSelectedFamilyUnit(familyUnit);
    setIsAddChildModalOpen(true);
  };

  // Handle form save
  const handleFormSave = async () => {
    await refreshData();

    // Close all modals
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setIsCreateFamilyUnitModalOpen(false);
    setIsAddChildModalOpen(false);

    // Reset selections
    setSelectedIndividual(null);
    setSelectedFamilyUnit(null);
  };

  // Get partner names for a family unit
  const getFamilyUnitPartnerNames = (familyUnit: FamilyUnit) => {
    const partner1 = individuals.find(ind => ind.id === familyUnit.partners[0]);
    const partner2 = individuals.find(ind => ind.id === familyUnit.partners[1]);

    return {
      partner1Name: partner1 ? `${partner1.firstName} ${partner1.lastName}` : 'Unknown',
      partner2Name: partner2 ? `${partner2.firstName} ${partner2.lastName}` : 'Unknown'
    };
  };

  const { t } = useTranslation();

  const renderListView = () => (
    <div className="family-data-container">
      <div className="members-list-container">
        <h3>{t('Family Members')}</h3>
        <div className="members-list">
          {individuals.length === 0 ? (
            <p>{t('No family members found. Click "Add New Member" to add someone.')}</p>
          ) : (
            <table className="members-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('Name')}</th>
                  <th>{t('Gender')}</th>
                  <th>{t('Birth Date')}</th>
                  <th>{t('Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {individuals.map((individual, index) => (
                  <tr key={individual.id}>
                    <td>{index + 1}</td>
                    <td>
                      {individual.firstName} {individual.middleName ? individual.middleName + ' ' : ''}
                      {individual.lastName}
                    </td>
                    <td>{individual.gender}</td>
                    <td>{individual.birthDate ? formatDate(individual.birthDate) : '-'}</td>
                    <td>
                      <button 
                        onClick={() => handleEditIndividual(individual)}
                        className="action-button edit-button"
                      >
                        {t('Edit')}
                      </button>
                      <button 
                        onClick={() => handleDeleteIndividual(individual)}
                        className="action-button delete-button"
                      >
                        {t('Delete')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      
      <div className="family-units-container">
        <h3>{t('Family Units')}</h3>
        <div className="family-units-list">
          {familyUnits.length === 0 ? (
            <p>{t('No family units found. Click "Create Family Unit" to connect two individuals.')}</p>
          ) : (
            <table className="family-units-table">
              <thead>
                <tr>
                  <th>{t('Partners')}</th>
                  <th>{t('Relationship')}</th>
                  <th>{t('Children')}</th>
                  <th>{t('Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {familyUnits.map(familyUnit => {
                  const { partner1Name, partner2Name } = getFamilyUnitPartnerNames(familyUnit);
                  const childrenCount = familyUnit.children ? familyUnit.children.length : 0;
                  
                  return (
                    <tr key={familyUnit.id}>
                      <td>{partner1Name} & {partner2Name}</td>
                      <td>{familyUnit.relationshipType}</td>
                      <td>{childrenCount}</td>
                      <td>
                        <button 
                          onClick={() => handleAddChildToFamily(familyUnit)}
                          className="action-button add-button"
                        >
                          {t('Add Child')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
      
      <div className="data-summary">
        <h3>{t('Family Tree Summary')}</h3>
        <ul>
          <li>{t('Individuals')}: {familyTree.individuals.length}</li>
          <li>{t('Family Units')}: {familyTree.familyUnits.length}</li>
          <li>{t('Child Relationships')}: {familyTree.childRelationships.length}</li>
          <li>{t('Emotional Relationships')}: {familyTree.emotionalRelationships.length}</li>
          <li>{t('Last Updated')}: {formatDate(familyTree.updatedAt)}</li>
        </ul>
      </div>
    </div>
  );

  const renderTreeView = () => (
    <div className="canvas-container">
      <p className="instruction-text">
        {t('Click on individuals to view details. Drag individuals to reposition them. Use mouse wheel or buttons to zoom in/out. Drag the canvas to pan around.')}
      </p>
      <FamilyTreeCanvas width={canvasSize.width} height={canvasSize.height} />
    </div>
  );

  if (loading) {
    return <div className="loading">{t('Loading family tree data...')}</div>;
  }

  return (
    <div className="family-tree-page" dir="rtl">
      <header className="app-header">
        <h1>{t('Family Tree Application')}</h1>
        <div className="user-controls">
          <button onClick={logout} className="logout-button">{t('Logout')}</button>
        </div>
      </header>
      
      <div className="controls-panel">
        <div className="data-controls">
          <button onClick={handleExportData} className="control-button">
            {t('Export JSON')}
          </button>
          <button onClick={handleExportPDF} className="control-button">
            {t('Export PDF')}
          </button>
          <button onClick={handleExportImage} className="control-button">
            {t('Export Image')}
          </button>
          <button onClick={handleExportExcel} className="control-button">
            {t('Export Excel')}
          </button>
          <button onClick={refreshData} className="control-button">
            {t('Refresh')}
          </button>
        </div>
        <div className="member-controls">
          <button onClick={() => setIsAddModalOpen(true)} className="primary-button">
            {t('Add New Member')}
          </button>
          <button onClick={() => setIsCreateFamilyUnitModalOpen(true)} className="primary-button">
            {t('Add Family Unit')}
          </button>
          <button onClick={() => setIsAddChildModalOpen(true)} className="primary-button">
            {t('Add Child to Family')}
          </button>
        </div>
      </div>

      <div className="view-tabs">
        <button 
          className={`tab-button ${activeView === 'tree' ? 'active' : ''}`}
          onClick={() => setActiveView('tree')}
        >
          {t('Tree View')}
        </button>
        <button 
          className={`tab-button ${activeView === 'list' ? 'active' : ''}`}
          onClick={() => setActiveView('list')}
        >
          {t('List View')}
        </button>
      </div>

      <div className="view-container">
        {activeView === 'tree' ? renderTreeView() : renderListView()}
      </div>
      
      {/* Add Member Modal */}
      <Modal 
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title={t('Add New Family Member')}
        size="large"
      >
        <IndividualForm 
          onSave={handleFormSave}
          onCancel={() => setIsAddModalOpen(false)}
        />
      </Modal>
      
      {/* Edit Member Modal */}
      <Modal 
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title={t('Edit Family Member')}
        size="large"
      >
        {selectedIndividual && (
          <IndividualForm 
            individual={selectedIndividual}
            onSave={handleFormSave}
            onCancel={() => setIsEditModalOpen(false)}
          />
        )}
      </Modal>
      
      {/* Create Family Unit Modal */}
      <Modal
        isOpen={isCreateFamilyUnitModalOpen}
        onClose={() => setIsCreateFamilyUnitModalOpen(false)}
        title={t('Create Family Unit')}
        size="large"
      >
        <RelationshipForm
          individuals={individuals}
          familyUnits={familyUnits}
          onSave={handleFormSave}
          onCancel={() => setIsCreateFamilyUnitModalOpen(false)}
          mode="family-unit"
        />
      </Modal>
      
      {/* Add Child Modal */}
      <Modal
        isOpen={isAddChildModalOpen}
        onClose={() => {
          setIsAddChildModalOpen(false);
          setSelectedFamilyUnit(null);
        }}
        title={t('Add Child to Family')}
        size="large"
      >
        <RelationshipForm
          individuals={individuals}
          familyUnits={familyUnits}
          onSave={handleFormSave}
          onCancel={() => {
            setIsAddChildModalOpen(false);
            setSelectedFamilyUnit(null);
          }}
          mode="add-child"
          selectedFamilyUnit={selectedFamilyUnit || undefined}
        />
      </Modal>
      
      {/* Delete Confirmation Modal */}
      <Modal 
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title={t('Confirm Delete')}
        size="small"
      >
        <div className="confirmation-dialog">
          <p>
            {t('Are you sure you want to delete')} {selectedIndividual?.firstName} {selectedIndividual?.lastName}? 
            {t('This action cannot be undone.')}
          </p>
          <div className="confirmation-actions">
            <button onClick={confirmDelete} className="primary-button danger">
              {t('Delete')}
            </button>
            <button onClick={() => setIsDeleteModalOpen(false)} className="secondary-button">
              {t('Cancel')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default FamilyTreePage;
